import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Table, Button, Space, message, Popconfirm, Tag, Modal, Form, Input, Select, Switch, Row, Col } from 'antd';
import { PlusOutlined, DeleteOutlined, ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import demoModulesApi, { DemoModule, DemoField } from '../../lib/demoModulesApi';

const FieldBuilderPage = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState<DemoModule | null>(null);
  const [fields, setFields] = useState<DemoField[]>([]);
  const [loading, _setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingField, setEditingField] = useState<DemoField | null>(null);
  const [form] = Form.useForm();

  const loadModule = async () => {
    if (!moduleId) return;
    try {
      const data = await demoModulesApi.getModule(moduleId);
      setModule(data);
      setFields(data.fields || []);
    } catch (error: any) {
      message.error('Failed to load module');
    }
  };

  useEffect(() => {
    loadModule();
  }, [moduleId]);

  const handleCreate = () => {
    setEditingField(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (field: DemoField) => {
    setEditingField(field);
    form.setFieldsValue(field);
    setModalVisible(true);
  };

  const handleDelete = async (fieldId: string) => {
    if (!moduleId) return;
    try {
      await demoModulesApi.deleteField(moduleId, fieldId);
      message.success('Field deleted');
      loadModule();
    } catch (error: any) {
      message.error('Failed to delete field');
    }
  };

  const handleSubmit = async (values: any) => {
    if (!moduleId) return;
    try {
      if (editingField) {
        await demoModulesApi.updateField(moduleId, editingField.id, values);
        message.success('Field updated');
      } else {
        await demoModulesApi.createField(moduleId, values);
        message.success('Field created');
      }
      setModalVisible(false);
      loadModule();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const columns = [
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
      render: (text: string, record: DemoField) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <code style={{ fontSize: 11, color: '#666' }}>{record.key}</code>
        </div>
      ),
    },
    {
      title: 'Type',
      key: 'type',
      render: (_: any, record: DemoField) => (
        <Space>
          <Tag color="blue">{record.dataType}</Tag>
          <Tag>{record.fieldType}</Tag>
        </Space>
      ),
    },
    {
      title: 'Validation',
      key: 'validation',
      render: (_: any, record: DemoField) => (
        <Space>
          {record.isRequired && <Tag color="red">Required</Tag>}
          {record.isUnique && <Tag color="orange">Unique</Tag>}
          {record.isReadonly && <Tag>Readonly</Tag>}
        </Space>
      ),
    },
    {
      title: 'Display',
      key: 'display',
      render: (_: any, record: DemoField) => (
        <Space>
          {record.inListView && <Tag color="green">List</Tag>}
          {record.inCreateForm && <Tag color="blue">Create</Tag>}
          {record.inEditForm && <Tag color="purple">Edit</Tag>}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: DemoField) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete field?"
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/demo/module-builder')}
            style={{ marginBottom: 16 }}
          >
            Back to Modules
          </Button>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <h2 style={{ margin: 0 }}>{module?.name} - Fields</h2>
              <p style={{ margin: '4px 0 0', color: '#666' }}>
                Configure fields for this module
              </p>
            </div>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Add Field
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={fields}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      <Modal
        title={editingField ? 'Edit Field' : 'Add Field'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            isRequired: false,
            isUnique: false,
            isReadonly: false,
            isVisible: true,
            isSearchable: true,
            inListView: true,
            inCreateForm: true,
            inEditForm: true,
            dataType: 'string',
            fieldType: 'text',
            sortOrder: fields.length,
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Key" name="key" rules={[{ required: true }]}>
                <Input placeholder="field_name" disabled={!!editingField} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Label" name="label" rules={[{ required: true }]}>
                <Input placeholder="Field Name" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Data Type" name="dataType">
                <Select>
                  <Select.Option value="string">String</Select.Option>
                  <Select.Option value="number">Number</Select.Option>
                  <Select.Option value="date">Date</Select.Option>
                  <Select.Option value="boolean">Boolean</Select.Option>
                  <Select.Option value="choice">Choice</Select.Option>
                  <Select.Option value="reference">Reference</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Field Type" name="fieldType">
                <Select>
                  <Select.Option value="text">Text</Select.Option>
                  <Select.Option value="textarea">Textarea</Select.Option>
                  <Select.Option value="number">Number</Select.Option>
                  <Select.Option value="select">Select</Select.Option>
                  <Select.Option value="date">Date</Select.Option>
                  <Select.Option value="datetime">DateTime</Select.Option>
                  <Select.Option value="checkbox">Checkbox</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Required" name="isRequired" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Unique" name="isUnique" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Readonly" name="isReadonly" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="In List View" name="inListView" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="In Create Form" name="inCreateForm" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="In Edit Form" name="inEditForm" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default FieldBuilderPage;
